import { useState } from "react";
import PageHero from "../components/PageHero";
import { submitEnquiry } from "@/services/enquiry";
import { DESTINATION_NAMES } from "@/pages/public/destinations/data";

const GUIDE_POINTS = [
  {
    icon: "ri-building-2-line",
    title: "5 Villa Models Explained",
    desc: "From the A-Shape Cottage to the Luxury Duplex — configurations, sizes and estimated costs at a glance.",
  },
  {
    icon: "ri-line-chart-line",
    title: "Realistic Returns & Payback",
    desc: "Transparent rental yield projections, occupancy assumptions and the 2.5–4 year payback maths, no hype.",
  },
  {
    icon: "ri-home-5-line",
    title: "Resort Management Model",
    desc: "How Nomad Livings operates, markets and fills your villa year-round across staycations, Airbnb and boutique resorts.",
  },
  {
    icon: "ri-hand-coin-line",
    title: "Step-by-Step Purchase Journey",
    desc: "Reserve, personalize, construct and start earning — with clear timelines and payment milestones.",
  },
  {
    icon: "ri-global-line",
    title: "Locations & Land Banks",
    desc: "A snapshot of active and upcoming villa projects across India and the criteria behind every site we choose.",
  },
  {
    icon: "ri-community-line",
    title: "NRI & Joint Ownership",
    desc: "Frameworks for NRI passive income, co-ownership and professional management while you stay abroad.",
  },
];

const INTEREST_OPTIONS = [
  "Staycation / Weekend Villa",
  "Airbnb / Short-Term Rental",
  "Boutique Resort Villa",
  "Holiday Home",
  "NRI Passive Income",
  "Not sure yet",
];

export default function InvestmentGuide() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("company_alt") as string) || "";
    if (honeypot.trim() !== "") {
      setStatus("success");
      return;
    }

    const payload = {
      full_name: String(formData.get("name") || "").trim(),
      email: String(formData.get("email") || "").trim() || undefined,
      phone: String(formData.get("phone") || "").trim() || undefined,
      message: String(formData.get("message") || "").trim() || undefined,
      investment_type: String(formData.get("interest") || "") || undefined,
      revenue_model: String(formData.get("location") || "") || undefined,
      page: "investment-guide",
    };

    const result = await submitEnquiry(payload);
    if (result.ok) {
      setStatus("success");
      setFormError("");
      form.reset();
    } else {
      setStatus("error");
      setFormError(result.message);
    }
  }

  return (
    <div className="w-full">
      <PageHero
        eyebrow="Investment Guide"
        title={
          <>
            Download the <span className="italic text-accent-300">2026 Investment Guide</span>
            &amp; Brochure
          </>
        }
        subtitle="Everything you need to evaluate a modular A-frame villa investment — models, returns, management and locations — in one clear, no-jargon guide."
      />

      {/* What's inside */}
      <section className="w-full py-12 md:py-16 bg-background-50">
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto">
            <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.22em] uppercase text-accent-600">
              <i className="ri-file-text-line"></i> What's Inside
            </span>
            <h2 className="font-heading text-2xl md:text-4xl text-foreground-950 mt-3">
              A Complete Guide to Villa Investment
            </h2>
            <p className="text-foreground-600 text-sm md:text-base mt-3 leading-relaxed">
              Whether you're a first-time investor or an NRI building a passive-income portfolio,
              the brochure walks you through every decision you need to make.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {GUIDE_POINTS.map((p) => (
              <div
                key={p.title}
                className="rounded-2xl bg-background-100 border border-background-200 p-5 hover:border-primary-300 transition-colors"
              >
                <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-primary-100 text-primary-700">
                  <i className={`${p.icon} text-xl`}></i>
                </span>
                <h3 className="font-heading text-base text-foreground-950 mt-4">{p.title}</h3>
                <p className="text-sm text-foreground-600 mt-1.5 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Download form */}
      <section className="w-full py-12 md:py-16 bg-background-100">
        <div className="w-full px-4 md:px-8 max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-start">
          {/* Left: brochure preview */}
          <div className="lg:sticky lg:top-24">
            <div className="relative rounded-3xl overflow-hidden border border-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Premium%20investment%20brochure%20cover%20mockup%20for%20modular%20A-frame%20wooden%20villas%20in%20India%2C%20elegant%20minimal%20layout%20with%20earthy%20green%20and%20warm%20gold%20accents%2C%20luxury%20villa%20photography%20on%20the%20cover%2C%20professional%20real%20estate%20print%20design%2C%20high%20detail&width=900&height=640&seq=guide-cover-2026&orientation=landscape"
                alt="Nomad Livings 2026 Investment Guide cover"
                className="w-full h-auto object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-5">
                <p className="font-heading text-lg text-background-50">Nomad Livings</p>
                <p className="text-xs text-background-200/90">
                  The 2026 Investment Guide · A-Frame Villa Investment
                </p>
              </div>
            </div>

            <div className="mt-5 flex items-start gap-3 rounded-2xl bg-primary-100 border border-primary-200 p-4">
              <span className="flex items-center justify-center w-10 h-10 rounded-full bg-accent-500 text-background-50 shrink-0">
                <i className="ri-mail-send-line text-lg"></i>
              </span>
              <p className="text-sm text-foreground-700 leading-relaxed">
                Instant delivery. The download link and brochure are emailed to you right after you
                submit — no calls, no sales pressure.
              </p>
            </div>
          </div>

          {/* Right: form */}
          <div className="rounded-3xl bg-background-50 border border-background-200 p-5 md:p-8">
            {status === "success" ? (
              <div className="rounded-2xl bg-primary-50 border border-primary-200 p-8 text-center">
                <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary-100 text-primary-700">
                  <i className="ri-check-line text-2xl"></i>
                </span>
                <h3 className="font-heading text-xl text-foreground-950 mt-4">
                  Your Guide Is on the Way!
                </h3>
                <p className="text-sm text-foreground-600 mt-2 leading-relaxed">
                  We've emailed your download link for the 2026 Investment Guide. If it doesn't
                  arrive in a few minutes, check your spam folder.
                </p>
                <button
                  onClick={() => setStatus("idle")}
                  className="mt-5 text-sm font-semibold text-primary-700 hover:text-primary-800"
                >
                  Send it again
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                <div className="flex items-start gap-4 mb-2">
                  <span className="flex items-center justify-center w-11 h-11 rounded-2xl bg-primary-100 text-primary-700 shrink-0">
                    <i className="ri-download-2-line text-xl"></i>
                  </span>
                  <div>
                    <h3 className="font-heading text-xl text-foreground-950">Get Your Free Copy</h3>
                    <p className="text-sm text-foreground-600 mt-0.5">
                      No obligation. Your details stay private.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-xs font-semibold text-foreground-800 mb-1.5">
                      Full Name
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      placeholder="Your name"
                      className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-xs font-semibold text-foreground-800 mb-1.5">
                      Phone / WhatsApp
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="+91 00000 00000"
                      className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs font-semibold text-foreground-800 mb-1.5">
                    Email Address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="interest" className="block text-xs font-semibold text-foreground-800 mb-1.5">
                      I'm interested in
                    </label>
                    <select
                      id="interest"
                      name="interest"
                      className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    >
                      {INTEREST_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="location" className="block text-xs font-semibold text-foreground-800 mb-1.5">
                      Preferred Destination
                    </label>
                    <select
                      id="location"
                      name="location"
                      className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    >
                      {DESTINATION_NAMES.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs font-semibold text-foreground-800 mb-1.5">
                    Anything specific you'd like to know? <span className="text-foreground-500 font-normal">(optional)</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={3}
                    maxLength={500}
                    placeholder="Budget, timeline, NRI purchase, etc."
                    className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                  ></textarea>
                </div>

                <input
                  type="text"
                  name="company_alt"
                  className="hp-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "error" && formError && (
                  <div className="rounded-lg bg-accent-100 border border-accent-300 px-4 py-3 text-sm text-accent-900">
                    <i className="ri-error-warning-line mr-1.5"></i>
                    {formError}
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full inline-flex items-center justify-center gap-2 text-sm md:text-base font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-6 py-3.5 hover:bg-primary-700 transition-colors"
                >
                  Send My Free Guide
                  <i className="ri-arrow-right-line"></i>
                </button>
                <p className="text-center text-[11px] text-foreground-600">
                  Free of charge. No hidden fees. We'll never share your details.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Reassurance strip */}
      <section className="w-full py-12 md:py-16 bg-background-50">
        <div className="w-full px-4 md:px-8 max-w-5xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: "ri-award-line", label: "15+ Years Real Estate" },
            { icon: "ri-map-pin-2-line", label: "Curated Land Banks" },
            { icon: "ri-line-chart-line", label: "28–40% Annual ROI" },
            { icon: "ri-shield-check-line", label: "Professionally Managed" },
          ].map((s) => (
            <div key={s.label} className="flex flex-col items-center text-center gap-2">
              <span className="flex items-center justify-center w-12 h-12 rounded-full bg-accent-100 text-accent-700">
                <i className={`${s.icon} text-xl`}></i>
              </span>
              <p className="text-sm font-semibold text-foreground-800">{s.label}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}